/**
 * guestWall — a signed-out tap on an account-only control opens the sign-in
 * screen instead of calling the server.
 *
 * 🔴 THE DEFECT (Blake's 10-01 staging walk). Signed out, Follow, the listing
 * heart, Save, a price vote, a comment heart, a comment send and a price check
 * each showed a toast ("Sign in to follow people") — and Follow also sent a
 * POST that came back 401. Blake's own handlers wrap every one of these in
 * `requireAuth(cb, location.hash)` (app.js), which is the sign-in wall.
 * writeGuard.ts stops those clicks at document capture, on purpose, so his
 * fake-success handlers never run; the installers that replaced them called the
 * server without asking whether anyone was signed in, and turned the 401 into a
 * toast. planning/v2/plan-guest-browsing-5.1.1v.md wants the wall, not a toast.
 *
 * THE FIX IS ONE QUESTION ASKED FIRST. app.js's `requireAuth` is a top-level
 * function declaration, so it is a real window property (rerenderKeepScroll is
 * called the same way). It runs its callback straight away for a signed-in
 * visitor and, for a guest, opens #wall and does NOT run it. That asymmetry is
 * the whole helper: the callback only flips a flag, so a guest gets the wall and
 * the caller returns before any request is built.
 *
 * `location.hash` is passed as the target so the wall's Sign In / Start Trading
 * buttons come back to where the person was — the same argument his own handlers
 * pass. Return-to-intent (replaying the tapped action after sign-in) is a
 * separate card and is not built here; the pending callback is the flag-setter
 * and nothing reads it for a guest.
 *
 * WHAT THIS DOES NOT REPLACE. The 401 branches in each installer stay: a
 * visitor whose session expired still reads as signed in to `store.authed`
 * (a boot-time snapshot), passes this check, and gets the server's 401 as a
 * toast. That is the right fallback for an expired session.
 *
 * If `requireAuth` is not there (app.js did not load), this answers "go ahead"
 * and the caller's own 401 handling is what remains — the behavior before this
 * file existed, never a silent dead control.
 */

interface WallWindow extends Window {
  requireAuth?: (cb: () => unknown, target?: string) => unknown;
}

/**
 * True when the tap may proceed (signed in, or the wall is unavailable). False
 * when the visitor is a guest and the sign-in wall has just been opened: the
 * caller must return without sending anything.
 */
export function signedInOrWall(win: Window): boolean {
  const gate = (win as WallWindow).requireAuth;
  if (typeof gate !== "function") return true;
  let signedIn = false;
  gate.call(win, () => {
    signedIn = true;
  }, win.location.hash);
  return signedIn;
}
